import type { Column, TicketSummary, User } from '@vd/types';
import { KCard } from '@vd/ui';
import { LayoutGrid } from 'lucide-react';
import { BoardViewEmpty, type BoardFilters } from './BoardViewEmpty';
import { cardTime } from '../../lib/When';
import { readLocalThumb } from '../annotations/localThumbs';
import styles from './CardsView.module.css';
import { useOpenCardSession } from '../ticket/cardSessionContext';

export interface CardsViewProps {
  tickets: TicketSummary[];
  columns: Column[];
  creatorsById: Map<string, User>;
  membersById: Map<string, User>;
  showThumbnails: boolean;
  onOpenTicket: (t: TicketSummary) => void;
  onCopyLink: (t: TicketSummary) => void;
  onOpenSource: (t: TicketSummary) => void;
  onStatusClick?: (t: TicketSummary, a: HTMLElement) => void;
  onTypeClick?: (t: TicketSummary, a: HTMLElement) => void;
  onOverflowClick?: (t: TicketSummary, a: HTMLElement) => void;
  onAssignClick?: (t: TicketSummary, a: HTMLElement) => void;
  bulkSelection?: Set<string>;
  onBulkToggle?: (ticketId: string, next: boolean) => void;
  /** Tickets currently being processed by a bulk operation (move/archive/
   * assign in flight). Renders a dim+spinner overlay on each so the user
   * sees feedback for long-running batches. */
  movingTicketIds?: Set<string>;
  /** Keyboard-focus model: which ticket is the J/K cursor on. Card click
   * also updates this via onFocus. The focus ring is the same one
   * KCard renders via its `selected` prop. */
  focusedTicketId?: string | null;
  onFocus?: (ticketId: string) => void;
  /**
   * This view's screen name for pins (TOADO-701, `lib/screenNames.ts`), for a
   * page that switches between views without a region of its own around them.
   */
  screen?: string;
  /** The filters narrowing the list, for saying so when they hide every ticket. */
  filters?: BoardFilters;
}

export function CardsView({ tickets, columns, creatorsById, membersById, showThumbnails, bulkSelection, onBulkToggle, movingTicketIds, focusedTicketId, onFocus, screen, filters, ...handlers }: CardsViewProps) {
  const openSession = useOpenCardSession();
  if (tickets.length === 0) {
    return <BoardViewEmpty icon={<LayoutGrid size={28} />} filters={filters} screen={screen} className={styles.empty} />;
  }
  const colById = new Map(columns.map((c) => [c.id, c]));

  return (
    <div className={styles.grid} data-toado-view={screen}>
      {tickets.map((t) => (
        <div
          key={t.id}
          // Click anywhere in the card area sets keyboard focus. Click
          // on the card body itself (KCard) also calls onOpenTicket;
          // that handler runs regardless. The focus update on click is
          // why j/k from a clicked card move from THAT card, not from
          // wherever the keyboard cursor was before.
          onClick={() => onFocus?.(t.id)}
        >
          <KCard
            ticket={t}
            creator={creatorsById.get(t.createdBy)}
            assignees={t.assignees.map((id) => membersById.get(id)).filter(Boolean) as User[]}
            column={colById.get(t.columnId)}
            showThumbnail={showThumbnails}
            thumbnailUrl={t.thumbnailUrl ?? readLocalThumb(t.id)}
            formatTime={cardTime}
            // KCard's `selected` ring serves double duty: bulk-selection
            // when a checkbox is on, keyboard-focus otherwise. Bulk wins
            // visually if both are true (the checkbox is the explicit
            // intent). When neither bulk nor focus, no ring.
            selected={focusedTicketId === t.id && !bulkSelection?.has(t.id)}
            onOpen={handlers.onOpenTicket}
            onCopyLink={handlers.onCopyLink}
            onSessionClick={openSession}
            onOpenSource={handlers.onOpenSource}
            onStatusClick={handlers.onStatusClick}
            onTypeClick={handlers.onTypeClick}
            onOverflowClick={handlers.onOverflowClick}
            onAssignClick={handlers.onAssignClick}
            bulkSelectable={!!bulkSelection}
            bulkSelected={bulkSelection?.has(t.id)}
            onBulkToggle={onBulkToggle}
            isMoving={movingTicketIds?.has(t.id)}
          />
        </div>
      ))}
    </div>
  );
}
